import {
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  ClipboardList,
  Code2,
  ContactRound,
  CreditCard,
  Database,
  Headset,
  LayoutGrid,
  Megaphone,
  MessageCircle,
  MessagesSquare,
  Star,
  Workflow,
} from "lucide-react";
import { siGoogleanalytics, siWhatsapp } from "simple-icons";
import { marchio } from "./brand";
import { APPS_IN_ARRIVO } from "./appsInArrivo";
import DataLayerApp from "./DataLayerApp";
import ModuloContattiApp from "./ModuloContattiApp";
import WhatsAppApp from "./WhatsAppApp";

/**
 * Le categorie delle app, nell'ordine dei filtri dell'elenco (una pillola per
 * categoria in "Da attivare"). Un'app con una categoria che non e' qui
 * finisce in fondo, sotto il suo nome o "Altro".
 */
export const CATEGORIE = [
  { nome: "Contatti", icon: ContactRound },
  { nome: "Canali", icon: MessagesSquare },
  { nome: "Appuntamenti", icon: CalendarDays },
  { nome: "Assistenza", icon: Headset },
  { nome: "Notifiche", icon: Bell },
  { nome: "CRM", icon: Building2 },
  { nome: "Dati", icon: Database },
  { nome: "Pagamenti", icon: CreditCard },
  { nome: "Automazioni", icon: Workflow },
  { nome: "Marketing", icon: Megaphone },
  { nome: "Feedback", icon: Star },
  { nome: "Sviluppatori", icon: Code2 },
];

export function categoriaDi(nome) {
  return CATEGORIE.find((c) => c.nome === nome) || { nome: nome || "Altro", icon: LayoutGrid };
}

/**
 * Le app raggruppate per categoria, nell'ordine di CATEGORIE.
 * @returns {{ nome: string, icon: Function, apps: object[] }[]}
 */
export function perCategoria(apps) {
  const gruppi = new Map();
  for (const app of apps) {
    const categoria = categoriaDi(app.category);
    if (!gruppi.has(categoria.nome)) gruppi.set(categoria.nome, { ...categoria, apps: [] });
    gruppi.get(categoria.nome).apps.push(app);
  }
  const ordine = (nome) => {
    const i = CATEGORIE.findIndex((c) => c.nome === nome);
    return i === -1 ? CATEGORIE.length : i;
  };
  return [...gruppi.values()].sort((a, b) => ordine(a.nome) - ordine(b.nome));
}

/**
 * Le app di Apps & Integrazioni.
 *
 * Per aggiungerne una basta una voce qui e la sua pagina di dettaglio:
 * l'elenco (IntegrationsPage) e la pagina di dettaglio
 * (IntegrationDetailPage, /bots/:id/integrazioni/:app) sono generici.
 *
 * - settingsKey: la sua chiave in GET/PUT /bots/:id/integrations
 * - category: una delle CATEGORIE qui sopra
 * - tint: il gradiente della sua icona (brand.jsx → TINTS)
 * - brand: per i servizi con un marchio, logo e colore ufficiali (marchio())
 * - tagline: la frase in testa alla sua pagina, detta come risultato
 * - examples: esempi di "quando" che il cliente puo' usare con un clic
 * - ready(settings): si puo' accendere subito, o prima va configurata?
 * - Detail: le sue impostazioni, sotto l'intestazione con l'interruttore
 * - quandoAttiva: facoltativa, la frase sotto l'interruttore acceso
 */
export const APPS = [
  {
    id: "modulo-contatti",
    settingsKey: "contact_form",
    title: "Modulo contatti",
    category: "Contatti",
    description:
      "L'assistente propone un modulo per farsi ricontattare, con il riassunto della chat già scritto.",
    tagline: "Trasforma una domanda in un contatto da richiamare",
    icon: ClipboardList,
    tint: "violet",
    examples: [
      "Quando chiede un preventivo o un prezzo per un lavoro su misura.",
      "Quando vuole fissare un sopralluogo o un appuntamento.",
      "Quando la domanda è troppo specifica per rispondere in chat.",
    ],
    ready: (settings) => settings.fields_count > 0,
    Detail: ModuloContattiApp,
  },
  {
    id: "whatsapp",
    settingsKey: "whatsapp",
    title: "Continua su WhatsApp",
    category: "Canali",
    description: "L'assistente mostra un pulsante che porta la conversazione sul vostro WhatsApp.",
    tagline: "Porta la conversazione sul tuo WhatsApp, con il messaggio già pronto",
    icon: MessageCircle,
    tint: "green",
    brand: marchio(siWhatsapp),
    examples: [
      "Quando chiede di parlare con una persona.",
      "Quando vuole mandare foto o documenti.",
      "Quando la richiesta è urgente.",
    ],
    ready: (settings) => Boolean(settings.phone),
    Detail: WhatsAppApp,
  },
  {
    id: "google-analytics",
    settingsKey: "datalayer",
    title: "Google Analytics",
    category: "Marketing",
    description:
      "Le interazioni con l'assistente arrivano in Google Analytics, attraverso il Tag Manager del sito.",
    tagline: "Misura in Google Analytics cosa fanno i visitatori con l'assistente",
    icon: BarChart3,
    tint: "orange",
    // Logo e colori ufficiali di Google Analytics: i due arancioni del marchio.
    brand: marchio(siGoogleanalytics, { sfondo: "linear-gradient(135deg, #F9AB00 0%, #E37400 100%)" }),
    examples: [],
    // Non deve aspettare niente: basta almeno un'interazione da inviare.
    ready: (settings) => (settings.actions || []).length > 0,
    quandoAttiva: "Il widget scrive le interazioni nel dataLayer del sito.",
    Detail: DataLayerApp,
  },
  // Le app in arrivo, in fondo: si vedono con «In arrivo» ma non si aprono.
  ...APPS_IN_ARRIVO,
];

// Indirizzi di prima: Google Analytics si chiamava "Google Tag Manager".
const VECCHI_ID = { "google-tag-manager": "google-analytics" };

export function appById(id) {
  const vero = VECCHI_ID[id] || id;
  return APPS.find((app) => app.id === vero) || null;
}
